import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, LinearProgress, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TablePagination, TableRow, TableSortLabel, Tooltip, Typography
} from '@mui/material';
import { AddRounded as AddIcon, OpenInNewRounded as OpenIcon } from '@mui/icons-material';
import { PAGE_SIZES, SOURCE_LABELS } from '../constants';
import { PrevYear, SortField, SortState, TimesheetAccess, TimesheetPagination, TimesheetRow } from '../types';
import { fmtMoment } from '../utils';

interface Props {
  rows: TimesheetRow[];
  pagination: TimesheetPagination;
  loading: boolean;
  error: string | null;
  sort: SortState;
  hasFilters: boolean;
  folderConfigured: boolean;
  access: TimesheetAccess;
  /** Текущий (рабочий) год табелей */
  year: number;
  /** Прошлый год, пока он доступен: рядом с табелем текущего года появляется табель прошлого */
  prev: PrevYear | null;
  /** Идёт массовое создание: единичное создание временно недоступно */
  runActive: boolean;
  /** Аптека, табель которой сейчас создаётся / открывается */
  busyNum: string | null;
  onSort: (key: SortField) => void;
  onPage: (page: number) => void;
  onLimit: (limit: number) => void;
  /** year не указан - табель текущего года */
  onOpen: (row: TimesheetRow, year?: number) => void;
  onCreate: (row: TimesheetRow) => void;
  onResetFilters: () => void;
  onRetry: () => void;
}

const BORDER = '#e2e8f0';

const COLUMNS: Array<{ key: SortField | null; label: string; width?: number | string }> = [
  { key: 'pharmacy', label: 'Аптека', width: 220 },
  { key: 'city', label: 'Регион', width: 150 },
  { key: 'status', label: 'Табель', width: 150 },
  { key: null, label: 'Файл' },
  { key: 'date', label: 'Создан', width: 200 }
];

const TimesheetTable: React.FC<Props> = ({
  rows, pagination, loading, error, sort, hasFilters, folderConfigured, access, year, prev, runActive, busyNum,
  onSort, onPage, onLimit, onOpen, onCreate, onResetFilters, onRetry
}) => {
  // колонка действий нужна, только если оператору есть что в ней показать
  const hasActions = access.open || access.settings;
  const columnCount = COLUMNS.length + (hasActions ? 1 : 0);
  const showError = Boolean(error) && !rows.length;
  const showEmpty = !loading && !error && !rows.length;
  // пока доступен прошлый год, рядом с табелем текущего года показывается табель прошлого
  const twoYears = Boolean(prev) && access.open;

  const renderOpen = (row: TimesheetRow, label: string, allowed: boolean, targetYear?: number) => {
    const busy = busyNum === row.pharmacyNum;
    const button = (
      <Button
        key={label} size="small" variant="outlined" disabled={!allowed || busy}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <OpenIcon sx={{ fontSize: 16 }} />}
        onClick={() => onOpen(row, targetYear)}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
      >
        {label}
      </Button>
    );
    // право на переход есть, но эта аптека вне его зоны - кнопка видна, но недоступна
    return allowed ? button : <Tooltip key={label} title="Эта аптека вне вашей зоны перехода в табель"><span>{button}</span></Tooltip>;
  };

  const renderCreate = (row: TimesheetRow) => {
    if (!access.settings || !folderConfigured) return null;
    const busy = busyNum === row.pharmacyNum;
    const button = (
      <Button
        key="create" size="small" variant="outlined" color="success" disabled={runActive || busy}
        startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <AddIcon sx={{ fontSize: 16 }} />}
        onClick={() => onCreate(row)}
        sx={{ textTransform: 'none', fontWeight: 600, borderRadius: 1.5, whiteSpace: 'nowrap' }}
      >
        Создать
      </Button>
    );
    return runActive ? <Tooltip key="create" title="Идёт массовое создание табелей"><span>{button}</span></Tooltip> : button;
  };

  const renderAction = (row: TimesheetRow) => {
    const buttons: React.ReactNode[] = [];
    if (access.open && row.status === 'created') {
      buttons.push(renderOpen(row, twoYears ? `Табель ${year}` : 'Открыть табель', row.canOpen));
    }
    if (twoYears && prev && row.prevCanOpen) {
      buttons.push(renderOpen(row, `Табель ${prev.year}`, true, prev.year));
    }
    if (row.status === 'missing') buttons.push(renderCreate(row));
    const visible = buttons.filter(Boolean);
    if (!visible.length) return null;
    return <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>{visible}</Box>;
  };

  return (
    <Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, overflow: 'hidden', bgcolor: '#fff' }}>
      <Box sx={{ height: 3 }}>{loading && <LinearProgress sx={{ height: 3 }} />}</Box>
      {error && (
        <Alert severity="error" sx={{ m: 1.5 }} action={<Button color="inherit" size="small" onClick={onRetry}>Повторить</Button>}>
          {error}
        </Alert>
      )}

      <TableContainer sx={{ maxHeight: 'calc(100vh - 430px)', minHeight: 240 }}>
        <Table size="small" stickyHeader sx={{ minWidth: 900, opacity: loading && rows.length ? 0.6 : 1, transition: 'opacity .15s' }}>
          <TableHead>
            <TableRow>
              {COLUMNS.map(col => (
                <TableCell
                  key={col.label} sortDirection={col.key && sort.sortBy === col.key ? sort.sortOrder : false}
                  sx={{ width: col.width, fontWeight: 700, fontSize: 12, color: '#475569', bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}`, whiteSpace: 'nowrap' }}
                >
                  {col.key ? (
                    <TableSortLabel
                      active={sort.sortBy === col.key} direction={sort.sortBy === col.key ? sort.sortOrder : 'asc'}
                      onClick={() => onSort(col.key as SortField)}
                    >
                      {col.label}
                    </TableSortLabel>
                  ) : col.label}
                </TableCell>
              ))}
              {hasActions && (
                <TableCell sx={{ width: twoYears ? 320 : 170, bgcolor: '#f8fafc', borderBottom: `1px solid ${BORDER}` }} />
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.pharmacyNum} hover sx={{ '& td': { borderBottom: '1px solid #f1f5f9', py: 0.9 } }}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: 13.5 }}>Аптека №{row.pharmacyNum}</Typography>
                  <Typography variant="caption" sx={{ color: '#94a3b8' }}>{row.username}</Typography>
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: '#475569' }}>{row.cityId || '-'}</TableCell>
                <TableCell>
                  {row.status === 'created'
                    ? <Chip size="small" label="Создан" sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 600 }} />
                    : <Chip size="small" label="Нет табеля" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 600 }} />}
                </TableCell>
                <TableCell sx={{ fontSize: 13, color: '#475569' }}>{row.fileName || '-'}</TableCell>
                <TableCell>
                  {row.createdAt ? (
                    <Tooltip title={[row.createdByName, row.source ? SOURCE_LABELS[row.source] : ''].filter(Boolean).join(' · ') || ''} placement="top-start">
                      <Typography variant="body2" sx={{ fontSize: 13, color: '#475569', whiteSpace: 'nowrap' }}>{fmtMoment(row.createdAt)}</Typography>
                    </Tooltip>
                  ) : (
                    <Typography variant="body2" sx={{ fontSize: 13, color: '#94a3b8' }}>-</Typography>
                  )}
                </TableCell>
                {hasActions && <TableCell align="right">{renderAction(row)}</TableCell>}
              </TableRow>
            ))}

            {showError && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">Данные не загружены</Typography>
                </TableCell>
              </TableRow>
            )}

            {showEmpty && (
              <TableRow>
                <TableCell colSpan={columnCount} align="center" sx={{ py: 6, color: '#64748b' }}>
                  <Typography variant="body2">
                    {hasFilters ? 'По заданным условиям аптек не найдено' : 'В вашей зоне нет аптек'}
                  </Typography>
                  {hasFilters && (
                    <Typography
                      variant="body2" onClick={onResetFilters}
                      sx={{ mt: 0.5, color: 'primary.main', cursor: 'pointer', display: 'inline-block', '&:hover': { textDecoration: 'underline' } }}
                    >
                      Сбросить фильтры
                    </Typography>
                  )}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div" count={pagination.total} page={Math.max(0, pagination.page - 1)} rowsPerPage={pagination.limit}
        rowsPerPageOptions={PAGE_SIZES}
        onPageChange={(_, p) => onPage(p + 1)}
        onRowsPerPageChange={e => onLimit(Number(e.target.value))}
        labelRowsPerPage="На странице:"
        labelDisplayedRows={({ from, to, count }) => `${from.toLocaleString('ru-RU')}–${to.toLocaleString('ru-RU')} из ${count.toLocaleString('ru-RU')}`}
        sx={{ borderTop: `1px solid ${BORDER}` }}
      />
    </Paper>
  );
};

export default React.memo(TimesheetTable);
